import { create } from 'zustand'

import type { MeetingRecordingConsentSurface } from '@scrambl/shared'

/**
 * Local UI gate for the first-use recording-consent dialog. Any start path
 * (record button, meeting reminder, tray) awaits `request()`; the single
 * MeetingRecordingConsentHost mounted per window renders the dialog and
 * settles the promise. Server state (whether consent exists) stays in
 * TanStack Query — this only owns the open dialog.
 */
interface RecordingConsentState {
  isOpen: boolean
  surface: MeetingRecordingConsentSurface
  resolver: ((acknowledged: boolean) => void) | null
  request: (surface: MeetingRecordingConsentSurface) => Promise<boolean>
  settle: (acknowledged: boolean) => void
}

export const useRecordingConsentStore = create<RecordingConsentState>(
  (set, get) => ({
    isOpen: false,
    surface: 'web',
    resolver: null,
    request: surface =>
      new Promise<boolean>(resolve => {
        // A second start attempt while the dialog is open supersedes the
        // first: the earlier caller is told "not acknowledged" and stops.
        get().resolver?.(false)
        set({ isOpen: true, surface, resolver: resolve })
      }),
    settle: acknowledged => {
      const { resolver } = get()
      set({ isOpen: false, resolver: null })
      resolver?.(acknowledged)
    },
  }),
)
